import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { Round, Bet } from '../api/types';
import { MouseEvent } from 'react';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  awaiting_payment: 'Aguardando pagamento',
  awaiting_manual_confirmation: 'Aguardando confirmação',
  paid: 'Pago',
  cancelled: 'Cancelado',
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: rounds } = useQuery({
    queryKey: ['rounds'],
    queryFn: async () => (await api.get<Round[]>('/rounds')).data,
  });

  const { data: bets } = useQuery({
    queryKey: ['my-bets'],
    queryFn: async () => (await api.get<Bet[]>('/my-bets')).data,
  });

  const { data: paymentConfig } = useQuery({
    queryKey: ['payment-config'],
    queryFn: async () => (await api.get<{ free_rounds: number[] }>('/payment-config')).data,
  });
  const freeRounds = paymentConfig?.free_rounds ?? [];

  const createBet = useMutation({
    mutationFn: async (roundId: number) =>
      (await api.post<Bet>('/bets', { round_id: roundId })).data,
    onSuccess: (bet) => {
      qc.invalidateQueries({ queryKey: ['my-bets'] });
      navigate(`/bet/${bet.id}`);
    },
    onError: (err: any) => {
      // Ex.: 409 "você já tem uma aposta nesta rodada". Revalida pra desabilitar o botão.
      qc.invalidateQueries({ queryKey: ['my-bets'] });
      alert(err.response?.data?.message || 'Não foi possível criar a aposta.');
    },
  });

  const deleteBet = useMutation({
    mutationFn: async (betId: string) => {
      await api.delete(`/bets/${betId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-bets'] }),
  });

  function handleDelete(e: MouseEvent, betId: string) {
    e.preventDefault();
    e.stopPropagation();
    if (confirm('Apagar este bolão em rascunho? Esta ação não pode ser desfeita.')) {
      deleteBet.mutate(betId);
    }
  }

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-xl font-bold mb-3">Comprar bolão</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {rounds?.map((r) => {
            const isFree = freeRounds.includes(r.id);
            // Rodada grátis: só 1 aposta por pessoa — bloqueia o botão se já apostou.
            const alreadyBet =
              isFree && bets?.some((b) => b.round_id === r.id && b.status !== 'cancelled');
            return (
            <div key={r.id} className="card">
              <h3 className="font-semibold">{r.name}</h3>
              <p className="text-xs text-slate-500 mt-1">
                Fecha em {new Date(r.betting_closes_at).toLocaleString('pt-BR')}
              </p>
              {isFree && (
                <p className="text-xs font-semibold text-brand-600 mt-1">
                  Grátis • 1 aposta por pessoa
                </p>
              )}
              <button
                className={`btn w-full mt-3 ${alreadyBet ? 'btn-confirmed' : 'btn-primary'}`}
                disabled={!r.is_open || createBet.isPending || alreadyBet}
                onClick={() => createBet.mutate(r.id)}
              >
                {!r.is_open
                  ? 'Encerrada'
                  : alreadyBet
                  ? 'Aposta Confirmada'
                  : isFree
                  ? 'Apostar grátis'
                  : 'Comprar (R$ 10)'}
              </button>
              <Link to={`/ranking/${r.id}`} className="block text-center text-xs mt-2 underline">
                Ver ranking
              </Link>
            </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold mb-3">Meus bolões</h2>
        <div className="space-y-2">
          {bets?.length === 0 && <p className="text-sm text-slate-500">Nenhum bolão ainda.</p>}
          {bets?.map((b) => (
            <Link
              key={b.id}
              to={
                b.status === 'awaiting_payment' || b.status === 'awaiting_manual_confirmation'
                  ? `/bet/${b.id}/pagamento`
                  : `/bet/${b.id}`
              }
              className="card flex items-center justify-between hover:bg-slate-50"
            >
              <div>
                <div className="font-semibold">Rodada {b.round_id}</div>
                <div className="text-xs text-slate-500">
                  {new Date(b.created_at).toLocaleString('pt-BR')}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div
                    className={
                      b.status === 'paid'
                        ? 'text-brand-600 font-semibold text-sm'
                        : 'text-amber-600 text-sm'
                    }
                  >
                    {STATUS_LABEL[b.status] || b.status}
                  </div>
                  {b.status === 'paid' && <div className="text-xs">{b.total_points} pts</div>}
                </div>
                {b.status === 'draft' && (
                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, b.id)}
                    disabled={deleteBet.isPending}
                    className="text-red-500 hover:text-red-700 text-xl leading-none px-2 py-1 rounded hover:bg-red-50"
                    aria-label="Apagar rascunho"
                    title="Apagar rascunho"
                  >
                    🗑
                  </button>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
